import {
  Button,
  Chip,
  MenuItem,
  Paper,
  Select,
  TableContainer,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useAuth } from "../../AuthContext";
import { envEndPoints } from "../../config";
import "./article-grid.css";

InstagramPostsGrid.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function InstagramPostsGrid({ clientId }) {
  const { getAuthHeader } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    const apiUrl = `${envEndPoints().baseUrl}/instagram-posts/?client_id=${clientId}&current_page=${
      page + 1
    }&page_size=${pageSize}`;

    fetch(apiUrl, { headers: getAuthHeader() })
      .then((res) => res.json())
      .then((data) => {
        setPosts(data.posts ?? []);
        setRowCount(typeof data.total_count === "number" ? data.total_count : 0);
      })
      .catch((err) => {
        console.error("Error fetching Instagram posts:", err);
        setPosts([]);
        setRowCount(0);
      })
      .finally(() => setLoading(false));
  }, [clientId, pageSize, page]);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(0);
  };

  return (
    <div style={{ width: "100%" }}>
      <TableContainer component={Paper}>
        <DataGrid
          autoHeight
          rows={posts}
          columns={[
            { field: "id", headerName: "ID", width: 90 },
            { field: "content", headerName: "Content", flex: 1, minWidth: 200 },
            { field: "author", headerName: "Author", width: 150 },
            {
              field: "postDatetime",
              headerName: "Date",
              width: 200,
              valueFormatter: (value) => {
                if (value == null || value === "") return "—";
                const d = new Date(value);
                return isNaN(d.getTime()) ? value : d.toUTCString();
              },
            },
            {
              field: "sentimentRating",
              headerName: "Sentiment",
              width: 120,
              renderCell: (params) => {
                const chipClass =
                  params.value === "Positive"
                    ? "chip-positive"
                    : params.value === "Neutral"
                    ? "chip-neutral"
                    : params.value === "Negative"
                    ? "chip-negative"
                    : "";
                return (
                  <Chip
                    label={params.value ?? "—"}
                    className={chipClass}
                    size="small"
                  />
                );
              },
            },
          ]}
          getRowId={(row) => row.id}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
          slots={{
            noRowsOverlay: () => (
              <div style={{ height: 52, display: "flex", alignItems: "center", justifyContent: "center" }}>
                No rows
              </div>
            ),
          }}
          sx={{
            "--DataGrid-overlayHeight": "52px",
            "& .MuiDataGrid-overlayWrapperInner": { height: "52px !important" },
            "& .MuiDataGrid-overlay": { height: "52px !important", minHeight: "52px !important" },
            ...(rowCount === 0 && {
              "& .MuiDataGrid-virtualScrollerContent": {
                height: "0 !important",
                minHeight: "0 !important",
              },
            }),
          }}
        />
        <div className="CustomPagination">
          <Select
            labelId="instagram-page-size-label"
            id="instagram-page-size"
            value={pageSize}
            onChange={handlePageSizeChange}
            label="Page Size"
          >
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
          <Button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            variant="contained"
            sx={{ mr: 2 }}
          >
            Previous
          </Button>
          <Typography>Page {page + 1}</Typography>
          <Button
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * pageSize >= rowCount}
            variant="contained"
            sx={{ ml: 2 }}
          >
            Next
          </Button>
        </div>
      </TableContainer>
    </div>
  );
}

export default InstagramPostsGrid;
